"use client";
import React from "react";
import GameCardDetailed from "@/components/game/card/detailed";
import {
  ApiResponse,
  Scoreboard as ScoreboardResponse,
  ScoreboardGame,
  getDefaultYear,
  SEASON_DURATION,
  Sport,
  GameStatus,
  ReducedGameStatus,
  BestOdds,
} from "common";
import { GamesByStatus, filterGamesByStatus, groupGamesByStatus2 } from "@/utils/game";
import NotFound from "@/components/primitives/not-found";
import { ScoreGroup } from "@/components/global/scoreboard";
import LeagueKickoff from "@/components/global/league-kickoff";
import { useRouter } from "next-nprogress-bar";
import { convertDateToStringWithoutTime, toISOStartOfDay } from "@/utils";
import Select from "@/components/primitives/select";
import DatePicker from "@/components/primitives/date-picker";
import DateTabber from "@/components/primitives/date-tabber";
import useFetch from "@/utils/fetch";
import { useParamUpdater } from "@/utils/hooks/navigation";
import { GameProvider } from "@/utils/providers/game";
import { useScoreboardUpdate } from "@/utils/hooks/scoreboard-update";

// const GameCardDetailedMemo = React.memo(GameCardDetailed);
interface ScoreboardFilterParams {
  sport: Sport;
  date: string;
}

type GameView = "all" | "pregame" | "live" | "final";
function formatGameStatusView(status: string): string {
  switch (status) {
    case "Scheduled":
      return "Pregame";
    case "InProgress":
      return "Live";
    default:
      return "Final";
  }
}
export default function ScoreboardGrid({
  params,
  initialData,
}: {
  params: ScoreboardFilterParams;
  initialData: ScoreboardGame[];
}) {
  const [games, setGames] = React.useState<ScoreboardGame[]>(initialData);
  const [selectedView, setSelectedView] = React.useState<GameView>("all");
  const router = useRouter();
  const { createParamString } = useParamUpdater();
  useScoreboardUpdate(games, setGames);
  

  const [statusUpdated, setStatusUpdated] = React.useState<boolean>(false);
  // let request: string = "";
  


  const today = toISOStartOfDay(convertDateToStringWithoutTime(new Date()));
  const date = params.date !== undefined ? toISOStartOfDay(params.date) : today;
  const todayDate = new Date(today);
  const selectedDate = new Date(date);
  const isToday: boolean = todayDate.getTime() === selectedDate.getTime();
  const isPast = selectedDate < todayDate;
  const isFuture = selectedDate > todayDate;
 

  // if (!isPast) {
  //   let idsToGetBestOdds: number[] = [];
  //   for (const game of initialData) {
  //     idsToGetBestOdds.push(game.id);
  //   }
  //   const gameIDs = idsToGetBestOdds.join(',');
  //   request = `${process.env.NEXT_PUBLIC_API_URL}/api/sports/${params.sport}/games/best-odds/?gameIDs=${gameIDs}`;
  // }
  // const { data: bestOddsData, isLoading, isError, error } = useFetch<
  //   BestOdds[][] | undefined
  // >(undefined, request, [], !request);

  // // Overwrite best odds for all games.
  // React.useEffect(() => {
  //   if (bestOddsData && !isLoading && !isError) {
  //     const updatedGames = games.map((game, index) => {
  //       const bestOdds = bestOddsData[index];
  //       return { ...game, bestOdds };
  //     });
  //     setGames(updatedGames);
  //   } else if (isError) {
  //     console.error('Error fetching best odds:', error);
  //   }
  // }, [bestOddsData, isLoading, isError]);

  const gamesByStatus = React.useMemo(() => {
    return groupGamesByStatus2(games);
  }, [games]);

  const hasGamesWithDifferentStatus: boolean = Object.keys(gamesByStatus).length !== 0;
  const disableGrouping: boolean = !isToday || !hasGamesWithDifferentStatus
  const disableViewSelection: boolean = !isToday || !hasGamesWithDifferentStatus;

  const viewOptions: Option[] = [
    {
      label: "All Games",
      value: "all",
    },
    {
      label: "Pregame",
      value: "Pregame",
    },
    {
      label: "Live",
      value: "Live",
    },
    {
      label: "Final",
      value: "Final",
    },
  ];
  function changeDate(date: Date) {
    const url = createParamString(convertDateToStringWithoutTime(date), 2);
    if (url) {
      router.replace(url, { scroll: false });
    }
  }

  
  const filter: JSX.Element = (
    <>
    <div className="flex-col flex desktop-grid:hidden gap-4">
        <div className="flex flex-row gap-4 justify-end">
          {!disableViewSelection && (

            <Select
            label="Select View"
            block
            value={selectedView}
            options={viewOptions}
            defaultValue={isPast ? "final" : isFuture ? "pregame" : "all"}
            onValueChange={(v) => setSelectedView(v as GameView)}
            />
          )}
          <DatePicker
            mode="single"
            align="end"
            value={new Date(date)}
            onValueChange={(v) => changeDate(v)}
          />
        </div>
        <DateTabber
          block
          value={new Date(date)}
          onValueChange={(v: Date) => changeDate(v)}
        />
      </div>
      <div className="hidden desktop-grid:flex gap-2">
        {!disableViewSelection && (

          <Select
          label="Select View"
          value={selectedView}
          options={viewOptions}
          defaultValue={isPast ? "final" : isFuture ? "pregame" : "all"}
          onValueChange={(v) => setSelectedView(v as GameView)}
          />
        )}
        <DateTabber
          value={new Date(date)}
          onValueChange={(v: Date) => changeDate(v)}
        />
        <DatePicker
          mode="single"
          align="end"
          value={new Date(date)}
          onValueChange={(v) => changeDate(v)}
        />
      </div>
    </>

  );

  const filterBlock: JSX.Element = (
    <>
    <div className="pt-0 desktop-grid:pt-8 flex flex-row justify-end">
            <div className="hidden desktop-grid:flex gap-4">
              {filter}
            </div>
          </div>
          <div className="flex flex-col gap-4 desktop-grid:hidden">
            {filter}
          </div>
          </>
  );


  if (!games || !games.length || Object.keys(gamesByStatus).length === 0) {
    const seasonDuration = SEASON_DURATION[params.sport as string];
    const currentDate = new Date(params.date);
    const showCountdown =
      currentDate >= new Date(seasonDuration.end) &&
      currentDate < new Date(seasonDuration.start);
    if (showCountdown) {
      return (
        <>
        <div className="pt-0 desktop-grid:pt-8 flex flex-row justify-end">
            <div className="hidden desktop-grid:flex gap-4">
              {filter}
            </div>
          </div>
          <div className="flex flex-col gap-4 desktop-grid:hidden">
            {filter}
          </div>
        <LeagueKickoff
          sport={params.sport}
          year={getDefaultYear(params.sport)}
          dateTime={seasonDuration.start}
          />
          </>
      );
    }
    return (
      <>
      {filterBlock}
        <NotFound label="No games found" icon="target-miss"></NotFound>
      </>
    );
  }

  return (
    <>
      {filterBlock}
      {Object.entries(gamesByStatus || {})
        .filter(([status, gameIndices]) => {
          if (selectedView === "all") {
            return gameIndices && gameIndices.length > 0;
          }
          return formatGameStatusView(status) === selectedView;
        })
        .map(([status, gameIndices], i) => {
          if (selectedView !== "all") {
            if (!gameIndices || gameIndices.length === 0) {
              return (
                <div key={status} className="flex justify-center items-center">
                  <NotFound label={`No ${selectedView} games found`} icon="target-miss" />
                </div>

              );
            }
          }
  
          return (
            <div className="flex flex-col gap-2" key={status}>
              <ScoreGroup
                hide={disableGrouping || selectedView !== "all"}
                collapsible={false}
                sport={params.sport}
                status={formatGameStatusView(status)}
                noLink
              >
                <div
                  className={`grid justify-items-center grid-cols-1 min-[768px]:grid-cols-2 min-[1158px]:grid-cols-3 gap-x-6 gap-8`}
                >
                  {gameIndices?.map((index) => (
                    <div
                      key={`${games[index].id}-${index}`}
                      className="relative w-full"
                    >
                      <GameProvider game={{ ...games[index] }}>

                      <GameCardDetailed index={index} />
                      </GameProvider>
                    </div>
                  ))}
                </div>
              </ScoreGroup>
            </div>
          );
        })}
  
   
    </>
  );
}  